import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  TrendingUp,
  MessageCircle,
  Globe,
  Music,
  FileDown,
  Trash2,
  Calendar,
  Store,
  ExternalLink,
  Database,
  BarChart3,
  RefreshCw,
  Building2,
  MapPin,
  Mail,
  FileText,
  Flag,
} from "lucide-react";
import { format, endOfMonth, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import jsPDF from "jspdf";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Shop Info
const SHOP_INFO = {
  name: "Gio parfum",
  code: "ITITLCRTQLQT",
  website: "www.equivalencia.it",
  tiktokUsername: "@gioparfum"
};

// Company Info
const COMPANY_INFO = {
  ragioneSociale: "EQUIVALENCIA DI GIONATA IVAN DEL GAUDIO",
  indirizzo: "Via Scipione Rovito 14",
  citta: "Napoli (NA)",
  cap: "80137",
  codiceDestinatario: "N92GLON",
  partitaIva: "10852151215",
  pec: "delgaudio.gionataivan@arubapec.it"
};

// Country data (fixed/fictitious)
const COUNTRY_DATA = [
  { country: "Italia", code: "IT", flag: "🇮🇹", amount: 1978766 },
  { country: "Francia", code: "FR", flag: "🇫🇷", amount: 422289 },
  { country: "Spagna", code: "ES", flag: "🇪🇸", amount: 123587 },
  { country: "Altri", code: "OTHER", flag: "🌍", amount: 15124 },
];

const TOTAL_COUNTRY_AMOUNT = COUNTRY_DATA.reduce((sum, c) => sum + c.amount, 0);

const Revenue = () => {
  const [dailyRevenues, setDailyRevenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), "yyyy-MM"));
  const [pdfMonth, setPdfMonth] = useState(format(new Date(), "yyyy-MM"));
  const [isPdfDialogOpen, setIsPdfDialogOpen] = useState(false);
  const [pdfType, setPdfType] = useState("commerciale");

  // Generate months for selection (last 24 months)
  const months = [];
  for (let i = 0; i < 24; i++) {
    const date = new Date();
    date.setMonth(date.getMonth() - i);
    months.push({
      value: format(date, "yyyy-MM"),
      label: format(date, "MMMM yyyy", { locale: it })
    });
  }

  useEffect(() => {
    loadRevenues();
  }, [selectedMonth]);

  const loadRevenues = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      const [year, month] = selectedMonth.split("-");
      const startDate = `${year}-${month}-01`;
      
      const res = await axios.get(`${API_URL}/api/orders?start_date=${startDate}&limit=2000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const orders = res.data.orders || res.data || [];
      
      const dailyData = {};
      
      orders.forEach(order => {
        if (order.is_live) return;
        
        const orderDate = order.created_at?.substring(0, 10);
        if (!orderDate) return;
        
        if (!orderDate.startsWith(selectedMonth)) return;
        
        if (!dailyData[orderDate]) {
          dailyData[orderDate] = {
            date: orderDate,
            whatsapp: 0,
            website: 0,
            tiktok_shop: 0,
            orders_count: 0
          };
        }
        
        const channel = order.channel || "manual";
        const amount = order.total_amount || 0;
        
        if (channel === "whatsapp") {
          dailyData[orderDate].whatsapp += amount;
        } else if (channel === "website") {
          dailyData[orderDate].website += amount;
        } else if (channel === "tiktok_shop") {
          dailyData[orderDate].tiktok_shop += amount;
        }
        
        dailyData[orderDate].orders_count++;
      });
      
      const sortedData = Object.values(dailyData).sort((a, b) => b.date.localeCompare(a.date));
      setDailyRevenues(sortedData);
      
    } catch (error) {
      toast.error("Errore nel caricamento del fatturato");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(value || 0);
  };

  const formatCurrencyPlain = (value) => {
    return new Intl.NumberFormat("it-IT", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0) + " €";
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat("it-IT").format(value || 0);
  };

  // Delete orders for a specific day and channel
  const handleDeleteDayChannel = async (date, channel) => {
    if (!confirm(`Sei sicuro di voler eliminare tutti gli ordini ${channel === 'all' ? 'del giorno' : `del canale ${channel}`} ${date}?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      
      const res = await axios.get(`${API_URL}/api/orders?start_date=${date}&limit=500`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const orders = res.data.orders || res.data || [];
      const toDelete = orders.filter(o => {
        const orderDate = o.created_at?.substring(0, 10);
        if (orderDate !== date) return false;
        if (channel === 'all') return true;
        return o.channel === channel;
      });
      
      for (const order of toDelete) {
        await axios.delete(`${API_URL}/api/orders/${order.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast.success(`Eliminati ${toDelete.length} ordini`);
      loadRevenues();
      
    } catch (error) {
      toast.error("Errore nell'eliminazione");
      console.error(error);
    }
  };

  // Open PDF dialog
  const openPdfDialog = (type) => {
    setPdfType(type);
    setIsPdfDialogOpen(true);
  };

  // Generate PDF for selected month
  const generatePDF = async () => {
    try {
      const token = localStorage.getItem("token");
      
      const [year, month] = pdfMonth.split("-");
      const startDate = `${year}-${month}-01`;
      
      const res = await axios.get(`${API_URL}/api/orders?start_date=${startDate}&limit=2000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const orders = res.data.orders || res.data || [];
      
      const dailyData = {};
      
      orders.forEach(order => {
        if (order.is_live) return;
        
        const orderDate = order.created_at?.substring(0, 10);
        if (!orderDate || !orderDate.startsWith(pdfMonth)) return;
        
        if (!dailyData[orderDate]) {
          dailyData[orderDate] = {
            date: orderDate,
            whatsapp: 0,
            website: 0,
            tiktok_shop: 0
          };
        }
        
        const channel = order.channel || "manual";
        const amount = order.total_amount || 0;
        
        if (channel === "whatsapp") {
          dailyData[orderDate].whatsapp += amount;
        } else if (channel === "website") {
          dailyData[orderDate].website += amount;
        } else if (channel === "tiktok_shop") {
          dailyData[orderDate].tiktok_shop += amount;
        }
      });
      
      const revenues = Object.values(dailyData).sort((a, b) => a.date.localeCompare(b.date));
      
      if (revenues.length === 0) {
        toast.error("Nessun dato per il mese selezionato");
        return;
      }
      
      const doc = new jsPDF();
      const monthLabel = format(parseISO(`${pdfMonth}-01`), "MMMM yyyy", { locale: it });
      
      // Header with company info
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(80);
      doc.text(COMPANY_INFO.ragioneSociale, 190, 10, { align: "right" });
      doc.text(`${COMPANY_INFO.indirizzo}, ${COMPANY_INFO.citta} ${COMPANY_INFO.cap}`, 190, 14, { align: "right" });
      doc.text(`P.IVA: ${COMPANY_INFO.partitaIva} | Cod. Dest.: ${COMPANY_INFO.codiceDestinatario}`, 190, 18, { align: "right" });
      
      // Main title
      doc.setTextColor(0);
      doc.setFontSize(24);
      doc.setFont("helvetica", "bold");
      doc.text("GIO PARFUM", 105, 35, { align: "center" });
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(80);
      doc.text("Report Fatturato", 105, 42, { align: "center" });
      
      // Data source note
      doc.setFontSize(8);
      doc.text("Dati importati da: TikTok Shop, WhatsApp Business, WooCommerce", 105, 48, { align: "center" });
      
      if (pdfType === "commerciale") {
        doc.setTextColor(0);
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("FATTURATO COMMERCIALE", 105, 60, { align: "center" });
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(`Sito Web + TikTok Shop - ${monthLabel}`, 105, 67, { align: "center" });
        
        doc.setDrawColor(100);
        doc.setLineWidth(0.5);
        doc.line(20, 73, 190, 73);
        
        let y = 83;
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("Data", 25, y);
        doc.text("Sito Web", 80, y);
        doc.text("TikTok Shop", 120, y);
        doc.text("Totale", 165, y);
        
        doc.setLineWidth(0.2);
        doc.line(20, y + 3, 190, y + 3);
        
        y += 10;
        doc.setFont("helvetica", "normal");
        
        let totalWebsite = 0;
        let totalTiktok = 0;
        
        revenues.forEach((rev) => {
          if (y > 270) {
            doc.addPage();
            y = 20;
          }
          
          totalWebsite += rev.website;
          totalTiktok += rev.tiktok_shop;
          const rowTotal = rev.website + rev.tiktok_shop;
          
          doc.text(format(parseISO(rev.date), "dd/MM/yyyy"), 25, y);
          doc.text(formatCurrencyPlain(rev.website), 80, y);
          doc.text(formatCurrencyPlain(rev.tiktok_shop), 120, y);
          doc.text(formatCurrencyPlain(rowTotal), 165, y);
          
          y += 7;
        });
        
        y += 5;
        doc.setLineWidth(0.5);
        doc.line(20, y, 190, y);
        y += 8;
        
        doc.setFont("helvetica", "bold");
        doc.text("TOTALE", 25, y);
        doc.text(formatCurrencyPlain(totalWebsite), 80, y);
        doc.text(formatCurrencyPlain(totalTiktok), 120, y);
        doc.text(formatCurrencyPlain(totalWebsite + totalTiktok), 165, y);
        
        doc.save(`fatturato_commerciale_${pdfMonth}.pdf`);
        toast.success("PDF Commerciale scaricato");
        
      } else {
        doc.setTextColor(0);
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("FATTURATO INTEGRALE", 105, 60, { align: "center" });
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(`WhatsApp + Sito Web + TikTok Shop - ${monthLabel}`, 105, 67, { align: "center" });
        
        doc.setDrawColor(100);
        doc.setLineWidth(0.5);
        doc.line(20, 73, 190, 73);
        
        let y = 83;
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.text("Data", 22, y);
        doc.text("WhatsApp", 55, y);
        doc.text("Sito Web", 95, y);
        doc.text("TikTok", 130, y);
        doc.text("Totale", 165, y);
        
        doc.setLineWidth(0.2);
        doc.line(20, y + 3, 190, y + 3);
        
        y += 10;
        doc.setFont("helvetica", "normal");
        
        let totalWhatsapp = 0;
        let totalWebsite = 0;
        let totalTiktok = 0;
        
        revenues.forEach((rev) => {
          if (y > 270) {
            doc.addPage();
            y = 20;
          }
          
          totalWhatsapp += rev.whatsapp;
          totalWebsite += rev.website;
          totalTiktok += rev.tiktok_shop;
          const rowTotal = rev.whatsapp + rev.website + rev.tiktok_shop;
          
          doc.text(format(parseISO(rev.date), "dd/MM/yy"), 22, y);
          doc.text(formatCurrencyPlain(rev.whatsapp), 55, y);
          doc.text(formatCurrencyPlain(rev.website), 95, y);
          doc.text(formatCurrencyPlain(rev.tiktok_shop), 130, y);
          doc.text(formatCurrencyPlain(rowTotal), 165, y);
          
          y += 7;
        });
        
        y += 5;
        doc.setLineWidth(0.5);
        doc.line(20, y, 190, y);
        y += 8;
        
        doc.setFont("helvetica", "bold");
        doc.text("TOTALE", 22, y);
        doc.text(formatCurrencyPlain(totalWhatsapp), 55, y);
        doc.text(formatCurrencyPlain(totalWebsite), 95, y);
        doc.text(formatCurrencyPlain(totalTiktok), 130, y);
        doc.text(formatCurrencyPlain(totalWhatsapp + totalWebsite + totalTiktok), 165, y);
        
        doc.save(`fatturato_integrale_${pdfMonth}.pdf`);
        toast.success("PDF Integrale scaricato");
      }
      
      setIsPdfDialogOpen(false);
      
    } catch (error) {
      toast.error("Errore nella generazione del PDF");
      console.error(error);
    }
  };

  // Calculate totals
  const totals = dailyRevenues.reduce((acc, day) => ({
    whatsapp: acc.whatsapp + day.whatsapp,
    website: acc.website + day.website,
    tiktok_shop: acc.tiktok_shop + day.tiktok_shop,
    orders: acc.orders + day.orders_count
  }), { whatsapp: 0, website: 0, tiktok_shop: 0, orders: 0 });

  const grandTotal = totals.whatsapp + totals.website + totals.tiktok_shop;

  // Calculate max value for bar chart scaling
  const maxChannelValue = Math.max(totals.whatsapp, totals.website, totals.tiktok_shop, 1);
  const maxCountryValue = Math.max(...COUNTRY_DATA.map(c => c.amount));

  // Get date range label
  const getDateRangeLabel = () => {
    const [year, month] = selectedMonth.split("-");
    const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
    const endDate = endOfMonth(startDate);
    return `${format(startDate, "d MMM yyyy", { locale: it })} - ${format(endDate, "d MMM yyyy", { locale: it })}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-gray-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="revenue-page" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="bg-white border border-gray-300 rounded-lg shadow-sm">
        <div className="p-5">
          {/* Top Row: Title + Shop Info + Company Info */}
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-5">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <BarChart3 className="w-5 h-5 text-gray-600" />
                <h1 className="text-xl font-semibold text-gray-900">
                  Report Fatturato
                </h1>
              </div>
              <p className="text-xs text-gray-500">
                Ultimo aggiornamento: {format(new Date(), "HH:mm - dd MMM yyyy", { locale: it })}
              </p>
            </div>
            
            {/* Shop + Company Info Box */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Shop Info */}
              <div className="bg-gray-50 border border-gray-200 rounded p-3 min-w-[220px]">
                <div className="flex items-start gap-2">
                  <Store className="w-4 h-4 text-gray-500 mt-0.5" />
                  <div className="flex-1 min-w-0 text-xs">
                    <p className="font-semibold text-gray-800">{SHOP_INFO.name}</p>
                    <p className="text-gray-500 font-mono">Shop ID: {SHOP_INFO.code}</p>
                    <a 
                      href={`https://${SHOP_INFO.website}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-gray-800 flex items-center gap-1 mt-0.5"
                    >
                      {SHOP_INFO.website}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Company Info */}
              <div className="bg-gray-50 border border-gray-200 rounded p-3 min-w-[280px]">
                <div className="flex items-start gap-2">
                  <Building2 className="w-4 h-4 text-gray-500 mt-0.5" />
                  <div className="flex-1 min-w-0 text-xs">
                    <p className="font-semibold text-gray-800 text-sm">{COMPANY_INFO.ragioneSociale}</p>
                    <p className="text-gray-600 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {COMPANY_INFO.indirizzo}, {COMPANY_INFO.citta} {COMPANY_INFO.cap}
                    </p>
                    <p className="text-gray-600 flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      P.IVA: {COMPANY_INFO.partitaIva} | Cod. Dest.: {COMPANY_INFO.codiceDestinatario}
                    </p>
                    <p className="text-gray-600 flex items-center gap-1 truncate">
                      <Mail className="w-3 h-3" />
                      {COMPANY_INFO.pec}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Data Source Notice */}
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 border border-gray-200 rounded text-xs mb-4">
            <Database className="w-4 h-4 text-gray-500" />
            <p className="text-gray-700">
              <span className="font-medium">Dati importati da:</span> TikTok Shop, WhatsApp Business, WooCommerce
            </p>
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Date Range */}
            <div className="flex items-center border border-gray-300 rounded overflow-hidden">
              <div className="px-2 py-2 bg-gray-100 border-r border-gray-300">
                <Calendar className="w-4 h-4 text-gray-500" />
              </div>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="border-0 min-w-[160px] focus:ring-0 text-sm" data-testid="month-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map(m => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Refresh Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={loadRevenues}
              className="border-gray-300 text-gray-600"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>

            <div className="flex-1" />

            {/* Action Buttons */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => openPdfDialog("commerciale")}
              className="border-gray-300 text-gray-700"
            >
              <FileDown className="w-4 h-4 mr-2" />
              PDF Commerciale
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => openPdfDialog("integrale")}
              className="border-gray-300 text-gray-700"
            >
              <FileDown className="w-4 h-4 mr-2" />
              PDF Integrale
            </Button>
          </div>
        </div>
      </div>

      {/* PDF Month Selection Dialog */}
      <Dialog open={isPdfDialogOpen} onOpenChange={setIsPdfDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              Scarica PDF {pdfType === "commerciale" ? "Commerciale" : "Integrale"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Seleziona il mese per il report:
            </p>
            <Select value={pdfMonth} onValueChange={setPdfMonth}>
              <SelectTrigger data-testid="pdf-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map(m => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsPdfDialogOpen(false)}>
                Annulla
              </Button>
              <Button onClick={generatePDF} className="bg-gray-800 hover:bg-gray-900 text-white">
                <FileDown className="w-4 h-4 mr-2" />
                Scarica PDF
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Country Sales Section */}
      <div className="bg-white border border-gray-300 rounded-lg shadow-sm p-5">
        <div className="flex items-center gap-2 mb-5">
          <Flag className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Vendite per Paese</h2>
          <span className="text-xs text-gray-500 ml-2">(Gross Sales)</span>
        </div>

        {/* Total */}
        <div className="mb-5 p-4 bg-gray-50 rounded border border-gray-200">
          <p className="text-xs text-gray-500 mb-1">Fatturato Totale per Paese</p>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(TOTAL_COUNTRY_AMOUNT)}</p>
        </div>

        {/* Country Bars */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Bar Chart */}
          <div className="space-y-4">
            {COUNTRY_DATA.map((country) => {
              const percentage = ((country.amount / TOTAL_COUNTRY_AMOUNT) * 100);
              return (
                <div key={country.code} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span>{country.flag}</span>
                      <span className="font-medium text-gray-800">{country.country}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-semibold text-gray-900">{formatCurrency(country.amount)}</span>
                      <span className="text-gray-500 ml-2 text-xs">({percentage.toFixed(2)}%)</span>
                    </div>
                  </div>
                  <div className="h-6 bg-gray-100 rounded overflow-hidden border border-gray-200">
                    <div 
                      className="h-full bg-gray-400 rounded transition-all duration-500"
                      style={{ width: `${(country.amount / maxCountryValue) * 100}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary Table */}
          <div className="bg-gray-50 rounded p-4 border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Riepilogo per Paese</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs font-semibold">Paese</TableHead>
                  <TableHead className="text-xs text-right font-semibold">Importo</TableHead>
                  <TableHead className="text-xs text-right font-semibold">%</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {COUNTRY_DATA.map((country) => {
                  const percentage = ((country.amount / TOTAL_COUNTRY_AMOUNT) * 100);
                  return (
                    <TableRow key={country.code}>
                      <TableCell className="py-2 text-sm">
                        <span className="mr-2">{country.flag}</span>
                        {country.country}
                      </TableCell>
                      <TableCell className="py-2 text-right font-medium text-sm">
                        {formatNumber(country.amount)} €
                      </TableCell>
                      <TableCell className="py-2 text-right text-gray-600 text-sm">
                        {percentage.toFixed(2)}%
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow className="border-t-2 border-gray-300 bg-gray-100">
                  <TableCell className="py-2 font-bold text-sm">Totale</TableCell>
                  <TableCell className="py-2 text-right font-bold text-sm">
                    {formatNumber(TOTAL_COUNTRY_AMOUNT)} €
                  </TableCell>
                  <TableCell className="py-2 text-right font-bold text-sm">100%</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Channel Sales + Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Sales Card */}
        <div className="lg:col-span-2 bg-white border border-gray-300 rounded-lg shadow-sm p-5">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-xs text-gray-500 mb-1">Fatturato Lordo (Periodo Selezionato)</p>
              <p className="text-3xl font-bold text-gray-900">{formatCurrency(grandTotal)}</p>
              <p className="text-xs text-gray-500 mt-1">{getDateRangeLabel()}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">{totals.orders} ordini</p>
            </div>
          </div>
          
          {/* Bar Chart */}
          <div className="space-y-4">
            <p className="text-sm font-medium text-gray-700">Vendite per Canale</p>
            
            {/* WhatsApp Bar */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-700">WhatsApp Business</span>
                </div>
                <span className="font-medium text-gray-900">{formatCurrency(totals.whatsapp)}</span>
              </div>
              <div className="h-6 bg-gray-100 rounded overflow-hidden border border-gray-200">
                <div 
                  className="h-full bg-gray-500 rounded transition-all duration-500"
                  style={{ width: `${(totals.whatsapp / maxChannelValue) * 100}%` }}
                />
              </div>
            </div>

            {/* Website Bar */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-700">WooCommerce (Sito Web)</span>
                </div>
                <span className="font-medium text-gray-900">{formatCurrency(totals.website)}</span>
              </div>
              <div className="h-6 bg-gray-100 rounded overflow-hidden border border-gray-200">
                <div 
                  className="h-full bg-gray-500 rounded transition-all duration-500"
                  style={{ width: `${(totals.website / maxChannelValue) * 100}%` }}
                />
              </div>
            </div>

            {/* TikTok Bar */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <Music className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-700">TikTok Shop</span>
                </div>
                <span className="font-medium text-gray-900">{formatCurrency(totals.tiktok_shop)}</span>
              </div>
              <div className="h-6 bg-gray-100 rounded overflow-hidden border border-gray-200">
                <div 
                  className="h-full bg-gray-500 rounded transition-all duration-500"
                  style={{ width: `${(totals.tiktok_shop / maxChannelValue) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="space-y-3">
          <Card className="bg-gray-800 border-0">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-gray-300 mb-1">
                <TrendingUp className="w-4 h-4" />
                <span className="text-xs font-medium">Totale Periodo</span>
              </div>
              <p className="text-2xl font-bold text-white">{formatCurrency(grandTotal)}</p>
              <p className="text-xs text-gray-400 mt-1">{totals.orders} ordini totali</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <MessageCircle className="w-4 h-4" />
                <span className="text-xs font-medium">WhatsApp</span>
              </div>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(totals.whatsapp)}</p>
              <p className="text-xs text-gray-500 mt-1">
                {grandTotal > 0 ? ((totals.whatsapp / grandTotal) * 100).toFixed(1) : 0}% del totale
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <Globe className="w-4 h-4" />
                <span className="text-xs font-medium">Sito Web</span>
              </div>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(totals.website)}</p>
              <p className="text-xs text-gray-500 mt-1">
                {grandTotal > 0 ? ((totals.website / grandTotal) * 100).toFixed(1) : 0}% del totale
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <Music className="w-4 h-4" />
                <span className="text-xs font-medium">TikTok Shop</span>
              </div>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(totals.tiktok_shop)}</p>
              <p className="text-xs text-gray-500 mt-1">
                {grandTotal > 0 ? ((totals.tiktok_shop / grandTotal) * 100).toFixed(1) : 0}% del totale
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Daily Table */}
      <Card className="border border-gray-300">
        <CardHeader className="border-b border-gray-200 bg-gray-50">
          <CardTitle className="text-base font-semibold flex items-center gap-2 text-gray-800">
            <Calendar className="w-4 h-4 text-gray-500" />
            Dettaglio Giornaliero
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {dailyRevenues.length === 0 ? (
            <div className="text-center py-10 text-gray-500 text-sm">
              Nessun dato per questo mese
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold text-gray-700">Data</TableHead>
                    <TableHead className="text-right font-semibold text-gray-700">WhatsApp</TableHead>
                    <TableHead className="text-right font-semibold text-gray-700">Sito Web</TableHead>
                    <TableHead className="text-right font-semibold text-gray-700">TikTok</TableHead>
                    <TableHead className="text-right font-semibold text-gray-700">Totale</TableHead>
                    <TableHead className="text-center font-semibold text-gray-700">Ordini</TableHead>
                    <TableHead className="text-right font-semibold text-gray-700">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dailyRevenues.map((day) => (
                    <TableRow key={day.date} className="hover:bg-gray-50">
                      <TableCell className="font-medium text-gray-800">
                        {format(parseISO(day.date), "EEE dd MMM", { locale: it })}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <span className={day.whatsapp > 0 ? "text-gray-900" : "text-gray-400"}>
                            {formatCurrency(day.whatsapp)}
                          </span>
                          {day.whatsapp > 0 && (
                            <button
                              onClick={() => handleDeleteDayChannel(day.date, "whatsapp")}
                              className="text-gray-400 hover:text-red-600 p-1"
                              title="Elimina ordini WhatsApp"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <span className={day.website > 0 ? "text-gray-900" : "text-gray-400"}>
                            {formatCurrency(day.website)}
                          </span>
                          {day.website > 0 && (
                            <button
                              onClick={() => handleDeleteDayChannel(day.date, "website")}
                              className="text-gray-400 hover:text-red-600 p-1"
                              title="Elimina ordini Sito Web"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <span className={day.tiktok_shop > 0 ? "text-gray-900" : "text-gray-400"}>
                            {formatCurrency(day.tiktok_shop)}
                          </span>
                          {day.tiktok_shop > 0 && (
                            <button
                              onClick={() => handleDeleteDayChannel(day.date, "tiktok_shop")}
                              className="text-gray-400 hover:text-red-600 p-1"
                              title="Elimina ordini TikTok"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-gray-900">
                        {formatCurrency(day.whatsapp + day.website + day.tiktok_shop)}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="bg-gray-100 px-2 py-1 rounded text-sm text-gray-700">
                          {day.orders_count}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <button
                          onClick={() => handleDeleteDayChannel(day.date, "all")}
                          className="text-gray-400 hover:text-red-600 hover:bg-red-50 p-2 rounded"
                          title="Elimina tutti gli ordini del giorno"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Revenue;
